import { createContext, useContext, useId, useMemo } from 'react';
import { Toast, ToastBody, ToastTitle, Toaster, useToastController } from '@fluentui/react-components';
import type { ToastIntent } from '@fluentui/react-components';

interface ToastContextValue {
  showToast: (title: string, body: string, intent?: ToastIntent) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const toasterId = useId();
  const { dispatchToast } = useToastController(toasterId);

  const value = useMemo<ToastContextValue>(() => ({
    showToast: (title, body, intent = 'info') => {
      dispatchToast(
        <Toast>
          <ToastTitle>{title}</ToastTitle>
          <ToastBody>{body}</ToastBody>
        </Toast>,
        { intent },
      );
    },
  }), [dispatchToast]);

  return (
    <ToastContext.Provider value={value}>
      <Toaster toasterId={toasterId} position="top-end" />
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within ToastProvider');
  return context;
};
